/**
 * Reducer para manejar el estado de las columnas Kanban
 * Implementa el principio de Responsabilidad Única (SRP)
 */
import {
  GET_COLUMNS,
  GET_COLUMNS_SUCCESS,
  GET_COLUMNS_ERROR,
  CREATE_COLUMN,
  CREATE_COLUMN_SUCCESS,
  CREATE_COLUMN_ERROR,
  UPDATE_COLUMN,
  UPDATE_COLUMN_SUCCESS,
  UPDATE_COLUMN_ERROR,
  DELETE_COLUMN,
  DELETE_COLUMN_SUCCESS,
  DELETE_COLUMN_ERROR,
  REORDER_COLUMNS,
  REORDER_COLUMNS_SUCCESS,
  REORDER_COLUMNS_ERROR,
} from './kanbanTypes';
import { areArraysEqual } from './utils/kanbanUtils';

/**
 * Estado inicial para el reducer de columnas
 */
const initialState = {
  items: [],
  loading: false,
  error: null,
  pagination: undefined
};

/**
 * Reducer para manejar el estado de las columnas Kanban
 */
const columnReducer = (state = initialState, action) => {
  switch (action.type) {
    // Obtener columnas
    case GET_COLUMNS:
      return {
        ...state,
        loading: true,
        error: null
      };
    case GET_COLUMNS_SUCCESS:
      // Verificar si es una operación de append (scroll infinito)
      if (action.meta && action.meta.isAppend) {
        // Para append mode, agregar las nuevas columnas sin duplicados
        const existingItems = state.items || [];
        const newItems = action.payload || [];
        
        // Evitar duplicados basándose en el ID
        const uniqueItems = [...existingItems];
        newItems.forEach(newItem => {
          const exists = existingItems.some(existing => String(existing.id) === String(newItem.id));
          if (!exists) {
            uniqueItems.push(newItem);
          }
        });

        return {
          ...state,
          items: uniqueItems,
          loading: false,
          pagination: {
            offset: action.meta.offset || 0,
            limit: action.meta.limit || 5,
            hasMore: action.meta.hasMore || false
          }
        };
      }

      // Evitar actualizaciones innecesarias si los datos son iguales (modo normal)
      if (areArraysEqual(state.items, action.payload)) {
        return {
          ...state,
          loading: false
        };
      }

      // Modo normal - reemplazar todas las columnas
      return {
        ...state,
        items: action.payload,
        loading: false,
        pagination: action.meta ? {
          offset: action.meta.offset || 0,
          limit: action.meta.limit || 5,
          hasMore: action.meta.hasMore || false
        } : undefined
      };
    case GET_COLUMNS_ERROR:
      return {
        ...state,
        items: [],
        loading: false,
        error: action.payload
      };

    // Crear columna
    case CREATE_COLUMN:
      return {
        ...state,
        loading: true
      };
    case CREATE_COLUMN_SUCCESS:
      // Verificar si la columna ya existe para evitar duplicados
      const columnExists = state.items.some(col => String(col.id) === String(action.payload.id));

      if (columnExists) {
        return {
          ...state,
          loading: false
        };
      }

      // Asegurarnos de que la nueva columna tenga un array de tickets vacío
      const newColumn = {
        ...action.payload,
        tickets: []
      };

      return {
        ...state,
        items: [...state.items, newColumn],
        loading: false
      };
    case CREATE_COLUMN_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Actualizar columna
    case UPDATE_COLUMN:
      return {
        ...state,
        loading: true
      };
    case UPDATE_COLUMN_SUCCESS:
      // Si tenemos sourceColumnId y targetColumnId, es un movimiento de ticket
      if (action.payload.sourceColumnId && action.payload.targetColumnId && action.payload.ticketId) {
        const { sourceColumnId, targetColumnId, ticketId, updatedTicket } = action.payload;

        // Verificar que las columnas existen
        const sourceColumn = state.items.find(col => String(col.id) === String(sourceColumnId));
        const targetColumn = state.items.find(col => String(col.id) === String(targetColumnId));

        if (!sourceColumn || !targetColumn) {
          return state;
        }

        // Encontrar el ticket a mover en la columna de origen
        const ticketToMove = sourceColumn.tickets?.find(ticket =>
          String(ticket.id) === String(ticketId)
        );

        // Si no encontramos el ticket en la columna de origen, usar el ticket actualizado directamente
        if (!ticketToMove && !updatedTicket) {
          return state;
        }

        // Actualizar el ticket con los datos nuevos si existen
        let updatedTicketData;

        if (updatedTicket) {
          // Si tenemos datos actualizados del ticket, usarlos
          updatedTicketData = updatedTicket;
        } else if (ticketToMove) {
          // Si tenemos el ticket original, actualizarlo con la nueva columna
          updatedTicketData = {
            ...ticketToMove,
            column_id: targetColumnId
          };
        } else {
          // Si no tenemos ni el ticket original ni datos actualizados, crear un ticket mínimo
          updatedTicketData = {
            id: ticketId,
            column_id: targetColumnId
          };
        }

        // Crear nuevas listas de tickets
        // Asegurarse de que el ticket se elimine de la columna de origen
        const newSourceTickets = Array.isArray(sourceColumn.tickets)
          ? sourceColumn.tickets.filter(ticket => String(ticket.id) !== String(ticketId))
          : [];

        // Asegurarse de que el ticket se agregue a la columna de destino sin duplicados
        let newTargetTickets = Array.isArray(targetColumn.tickets) ? [...targetColumn.tickets] : [];

        // Eliminar el ticket de la columna de destino si ya existe (para evitar duplicados)
        newTargetTickets = newTargetTickets.filter(ticket => String(ticket.id) !== String(ticketId));

        // Agregar el ticket actualizado al principio de la lista
        newTargetTickets = [updatedTicketData, ...newTargetTickets];

        // Crear una copia profunda del estado actual para evitar mutaciones
        const updatedItems = state.items.map(column => {
          // Si es la columna de origen, eliminar el ticket
          if (String(column.id) === String(sourceColumnId)) {
            return {
              ...column,
              tickets: newSourceTickets
            };
          }
          // Si es la columna de destino, agregar el ticket
          if (String(column.id) === String(targetColumnId)) {
            return {
              ...column,
              tickets: newTargetTickets
            };
          }
          // Para todas las demás columnas, eliminar el ticket si existe
          if (Array.isArray(column.tickets) && column.tickets.some(t => String(t.id) === String(ticketId))) {
            return {
              ...column,
              tickets: column.tickets.filter(t => String(t.id) !== String(ticketId))
            };
          }
          return column;
        });

        // Actualizar el estado con las columnas modificadas
        return {
          ...state,
          items: updatedItems,
          loading: false
        };
      }

      // Si no tiene sourceColumnId y targetColumnId, es una actualización normal de columna
      return {
        ...state,
        items: state.items.map(column =>
          column.id === action.payload.id ? action.payload : column
        ),
        loading: false
      };
    case UPDATE_COLUMN_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Eliminar columna
    case DELETE_COLUMN:
      return {
        ...state,
        loading: true
      };
    case DELETE_COLUMN_SUCCESS:
      // Obtener el ID de la columna eliminada
      const deletedColumnId = action.payload;

      // Eliminar la columna del estado
      return {
        ...state,
        items: state.items.filter(column => String(column.id) !== String(deletedColumnId)),
        loading: false
      };
    case DELETE_COLUMN_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Reordenar columnas
    case REORDER_COLUMNS:
      return {
        ...state,
        loading: true,
      };
    case 'REORDER_COLUMNS_OPTIMISTIC':
      return {
        ...state,
        items: action.payload
      };
    case REORDER_COLUMNS_SUCCESS:
      const updatedColumns = action.payload;
      
      if (!Array.isArray(updatedColumns) || updatedColumns.length === 0) {
        return {
          ...state,
          loading: false
        };
      }
      
      // Actualizar solo las columnas que vinieron en el payload, mantener las demás
      const updatedItems = state.items.map(existingColumn => {
        const updatedColumn = updatedColumns.find(col => 
          String(col.id) === String(existingColumn.id)
        );
        
        if (updatedColumn) {
          return {
            ...updatedColumn,
            tickets: updatedColumn.tickets || existingColumn.tickets || []
          };
        }
        
        return existingColumn;
      });
      
      // Agregar cualquier columna nueva que esté en el payload pero no en el estado actual
      updatedColumns.forEach(updatedColumn => {
        const existsInState = state.items.some(existingCol => 
          String(existingCol.id) === String(updatedColumn.id)
        );
        
        if (!existsInState) {
          updatedItems.push({
            ...updatedColumn,
            tickets: updatedColumn.tickets || []
          });
        }
      });
      
      return {
        ...state,
        items: updatedItems,
        loading: false
      };
    case REORDER_COLUMNS_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    default:
      return state;
  }
};

export default columnReducer;
